import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getAdmins, addAdmin, removeAdmin } from '../../services/adminService';
import { FaUserPlus, FaTrash, FaSpinner } from 'react-icons/fa';

const AdminManagement = () => {
  const { currentUser } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updating, setUpdating] = useState({});

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const adminList = await getAdmins();
      setAdmins(adminList);
      setError('');
    } catch (err) {
      setError(err.message);
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!newAdminEmail.trim()) {
      setError('Please enter an email address.');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const result = await addAdmin(newAdminEmail);
      setAdmins(prevAdmins => [...prevAdmins, result.admin]);
      setNewAdminEmail('');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveAdmin = async (uid) => {
    setUpdating(prev => ({ ...prev, [uid]: true }));
    setError('');
    try {
      await removeAdmin(uid);
      setAdmins(prevAdmins => prevAdmins.filter(admin => String(admin.id) !== String(uid)));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(prev => ({ ...prev, [uid]: false }));
    }
  };

  return (
    <div>
      <div className="admin-page-header">
        <h1>Admin Management</h1>
        <p>Add or remove administrator privileges for users.</p>
      </div>

      <div className="admin-card" style={{ marginBottom: '2rem' }}>
        <h2>Add New Admin</h2>
        <form onSubmit={handleAddAdmin} className="admin-add-form">
          <input
            type="email"
            value={newAdminEmail}
            onChange={(e) => setNewAdminEmail(e.target.value)}
            placeholder="Enter user's email to make admin"
            className="admin-form-input"
            required
          />
          <button type="submit" className="admin-btn admin-btn-success" disabled={isSubmitting}>
            {isSubmitting ? <FaSpinner className="spinning" /> : <FaUserPlus />} Add Admin
          </button>
        </form>
      </div>

      <div className="admin-card">
        <h2>Current Admins</h2>
        {loading && <div className="loading-spinner"><FaSpinner /> Loading admins...</div>}
        {error && <div className="admin-error-message">Error: {error}</div>}
        {!loading && (
          <div className="admin-table-container">
            {admins.length > 0 ? (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Display Name</th>
                    <th>User ID</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map(admin => (
                    <tr key={admin.id}>
                      <td>{admin.email}</td>
                      <td>{admin.displayName || 'N/A'}</td>
                      <td>{admin.id}</td>
                      <td className="actions-cell">
                        <button 
                          className="admin-btn admin-btn-danger" 
                          onClick={() => handleRemoveAdmin(admin.id)}
                          disabled={updating[admin.id] || String(admin.id) === String(currentUser?.id)}
                          title={String(admin.id) === String(currentUser?.id) ? 'Cannot remove yourself' : 'Remove Admin'}
                        >
                          {updating[admin.id] ? <FaSpinner className="spinning" /> : <FaTrash />} Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="admin-empty-state">
                <p>No administrators found.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminManagement;